import { Box, CircularProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';

import { TranslateText } from '../../../../../../application/language/TranslateText';
import { useI18n } from '../../../../../../application/language/useI18n';
import { IMainLayoutDomainStore } from '../../../../../../view/layout/main/store/domain/IMainLayoutDomainStore';
import { EmptyPage } from '../../../design/emptyPage/EmptyPage';
import { UnitListAdminCard } from './components/UnitListAdminCard';
import adminEmptyPage from './parts/adminEmptyPage.png';
import { UnitListCard } from './parts/UnitListCard';
import { IUnitListAdminPageDomain } from './store/IUnitListAdminPageDomain';

enum FilterValues {
  All = 'all',
  Published = 'published',
  NotPublished = 'notPublished',
}

export interface IUnitListAdminPage {
  layoutDomain: IMainLayoutDomainStore;
}

export const UnitListAdminPage = observer(({ domain }: { domain: IUnitListAdminPageDomain }) => {
  const { translate } = useI18n();

  useEffect(() => {
    domain.loadData();
  }, [domain]);

  const filteredGroups = domain.ui.filtredGroupedUnits.list
    .map((group) => {
      const { unitsList } = group;
      const filter = domain.ui.filterByIsPublished.value;

      const filteredUnits =
        filter === FilterValues.All
          ? unitsList
          : filter === FilterValues.Published
          ? unitsList.filter((unit) => unit.isPublished)
          : unitsList.filter((unit) => !unit.isPublished);

      return { ...group, unitsList: filteredUnits };
    })
    .filter((group) => group.unitsList.length > 0);

  const hasUnits = filteredGroups.length > 0;

  return (
    <>
      {domain.ui.isLoading.value ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {domain.ui.unitList.list.length === 0 ? (
            <EmptyPage
              image={adminEmptyPage}
              title={translate('learningAdminPage.createUnitLabel')}
              buttonTitle={translate('learningAdminPage.createUnit')}
              buttonAction={domain.redirectToCreateUnit}
            />
          ) : (
            <Box sx={{ display: 'flex', gap: '16px', marginTop: '48px' }}>
              <Box
                sx={{
                  flex: '0 0 28%',
                  gap: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'calc(100vh - 240px)',
                  overflowY: 'scroll',
                  paddingBottom: '16px',
                }}
              >
                {filteredGroups.map((group) => (
                  <UnitListCard key={group.groupName} title={group.groupName} units={group.unitsList} domain={domain} />
                ))}
              </Box>

              <Box
                sx={{
                  flex: '0 0 70%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  paddingRight: '4px',
                  height: 'calc(100vh - 240px)',
                  overflowY: 'scroll',
                  paddingBottom: '16px',
                }}
                id="units-container"
              >
                {!hasUnits && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      height: '100%',
                    }}
                  >
                    <Typography variant="h5">
                      <TranslateText i18nKey="learningAdminPage.noUnits" />
                    </Typography>
                  </Box>
                )}

                {filteredGroups.flatMap((group) =>
                  group.unitsList.map((unit) => (
                    <UnitListAdminCard
                      key={unit.id}
                      handleEdit={domain.onEditUnit}
                      handleDelete={domain.onDeleteUnit}
                      handlePublish={domain.publishUnit}
                      handleUnpublish={domain.unpublishUnit}
                      unit={unit}
                    />
                  )),
                )}
              </Box>
            </Box>
          )}
        </>
      )}
    </>
  );
});
